/**
 * EntityActions — Editar + Excluir consistente em todas as detail pages.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";

interface EntityActionsProps {
  /** Callback chamado ao clicar em Editar (geralmente abre Sheet) */
  onEdit?: () => void;
  /** Função async que deleta o recurso */
  onDelete?: () => Promise<unknown>;
  /** Rota pra navegar após delete (ex: "/clients") */
  redirectAfterDelete?: string;
  /** Query keys pra invalidar após delete */
  invalidateQueries?: string[];
  /** Nome do recurso pra mensagens ("cliente", "técnico", etc) */
  resourceLabel?: string;
  /** Esconder o botão Editar */
  hideEdit?: boolean;
  /** Esconder o botão Excluir */
  hideDelete?: boolean;
}

export function EntityActions({
  onEdit,
  onDelete,
  redirectAfterDelete,
  invalidateQueries = [],
  resourceLabel = "registro",
  hideEdit,
  hideDelete,
}: EntityActionsProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const del = useMutation({
    mutationFn: async () => {
      if (!onDelete) throw new Error("onDelete not provided");
      return onDelete();
    },
    onSuccess: () => {
      toast.success(`${capitalize(resourceLabel)} excluído`);
      invalidateQueries.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setConfirmOpen(false);
      if (redirectAfterDelete) navigate(redirectAfterDelete);
    },
    onError: (e: Error) => {
      toast.error("Falha ao excluir", { description: e.message });
    },
  });

  return (
    <>
      {!hideEdit && onEdit && (
        <Button variant="outline" onClick={onEdit}>
          <Pencil className="h-4 w-4" /> Editar
        </Button>
      )}
      {!hideDelete && onDelete && (
        <Button
          variant="outline"
          onClick={() => setConfirmOpen(true)}
          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 dark:text-rose-400 dark:hover:bg-rose-500/10"
        >
          <Trash2 className="h-4 w-4" /> Excluir
        </Button>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        variant="danger"
        title={`Excluir ${resourceLabel}?`}
        description="Esta ação não pode ser desfeita. Todos os dados relacionados serão impactados."
        confirmLabel="Sim, excluir"
        loading={del.isPending}
        onConfirm={() => del.mutate()}
      />
    </>
  );
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
